import {Component, Injector, OnInit} from '@angular/core';
import {Athlete} from "../../models/athlete";
import {ActivatedRoute, Router} from "@angular/router";
import {AthleteService} from "../../services/athlete/athlete.service";
import {Membership} from "../../models/membership";
import {Category} from "../../models/category";
import {CategoryService} from "../../services/category/category.service";
import {MembershipService} from "../../services/membership/membership.service";
import Swal from "sweetalert2";
import {BaseComponent} from "../base/base.component";
import {Season} from "../../models/season";
import * as moment from 'moment';
import {SeasonService} from "../../services/season/season.service";
import {normalizeSpaces, todayIsoDate} from "../../resources/person-validation";

type MembershipField = 'badgeDetails' | 'badgeExpiration' | 'federationName' | 'categoryId';
const MEMBERSHIP_FIELDS: MembershipField[] = ['badgeDetails', 'badgeExpiration', 'federationName', 'categoryId'];

@Component({
    selector: 'app-membership',
    templateUrl: './membership.component.html'
})
export class MembershipComponent extends BaseComponent implements OnInit {

    athleteId: string | undefined;
    athlete: Athlete = new Athlete();
    membership: Membership = new Membership();
    categories: Category[] = [];
    currentCategoryId: string | undefined;
    companyId: string | undefined;

    seasons: Season[] = [];
    currentSeasonId?: string;
    isAthleteUser: boolean = this.getUserType() === 'user';

    /** Stato dei due flag, gestiti come booleani nel form. */
    isRegistered = false;
    isSigned = false;

    loading = true;
    saving = false;
    sendingNotification = false;
    readonly today = todayIsoDate();

    private touched = new Set<MembershipField>();
    private submitted = false;

    constructor(public athleteService: AthleteService, public categoryService: CategoryService, public membershipService: MembershipService, public route: ActivatedRoute, public router: Router, public serviceInjector: Injector, public seasonService: SeasonService) {
        super(serviceInjector);
    }

    async ngOnInit() {
        this.athleteId = this.route.snapshot.params['athleteId'];
        try {
            this.athlete = await this.athleteService.getAthleteById(this.athleteId!);
            this.companyId = this.athlete.companyId;

            if (this.athlete.memberships.length > 0) this.membership = this.athlete.memberships[0];
            this.membership.badgeExpiration = this.membership.badgeExpiration
                ? moment(this.membership.badgeExpiration).format('YYYY-MM-DD')
                : '';
            this.isRegistered = this.toFlag(this.membership.registered);
            this.isSigned = this.toFlag(this.membership.signed);

            if (this.companyId) await this.loadSeasons();
            this.currentSeasonId = this.seasons.find(season => season.isCurrent === 1)?.id;
            if (this.companyId) this.categories = await this.categoryService.getCategories(this.companyId, false, {seasonId: this.currentSeasonId});

            this.categories = this.categories
                .filter((category: Category) => category.parentId !== null)
                .map((category: Category) => ({...category, name: `${category.parent!.name} - ${category.name}`}));

            this.currentCategoryId = this.athlete.seasons.find((season: Season) => season.isCurrent)?.AthleteSeason.categoryId;
            if (this.currentCategoryId) this.membership.categoryId = this.currentCategoryId;
            this.membership.categoryId = this.membership.categoryId ?? '';
        } catch (e) {
            console.error('error loading membership: ', e);
        } finally {
            this.loading = false;
        }
    }

    get backRoute(): any[] {
        return ['/users/profile', this.athleteId];
    }

    /** Il tesseramento esiste già (l'atleta ha almeno una membership salvata). */
    get hasMembership(): boolean {
        return this.athlete.memberships.length > 0;
    }

    get expirationStatus(): { text: string; classes: string } | null {
        const value = this.membership.badgeExpiration;
        if (!value || this.errorFor('badgeExpiration')) return null;
        const days = moment(value).startOf('day').diff(moment().startOf('day'), 'days');
        if (days < 0) return {text: 'Tesserino scaduto', classes: 'bg-red-50 text-red-700'};
        if (days <= 30) return {text: `Scade tra ${days} ${days === 1 ? 'giorno' : 'giorni'}`, classes: 'bg-amber-50 text-amber-800'};
        return null;
    }

    errorFor(field: MembershipField): string | null {
        const m = this.membership;
        switch (field) {
            case 'badgeDetails':
                return (m.badgeDetails || '').trim() ? null : 'Inserisci il numero del tesserino.';
            case 'badgeExpiration':
                if (!m.badgeExpiration) return 'Inserisci la data di scadenza.';
                return moment(m.badgeExpiration, 'YYYY-MM-DD', true).isValid() ? null : 'Inserisci una data valida.';
            case 'federationName':
                return (m.federationName || '').trim().length >= 2 ? null : 'Inserisci il nome della federazione.';
            case 'categoryId':
                return m.categoryId ? null : 'Seleziona la categoria.';
        }
    }

    showError(field: MembershipField): string | null {
        return this.touched.has(field) || this.submitted ? this.errorFor(field) : null;
    }

    markTouched(field: MembershipField): void {
        this.touched.add(field);
    }

    normalize(field: 'badgeDetails' | 'federationName'): void {
        this.membership[field] = normalizeSpaces(this.membership[field]);
    }

    /** I flag arrivano dal backend come 0/1, "0"/"1" o booleani. */
    private toFlag(value: unknown): boolean {
        return value === true || value === 'true' || Number(value) === 1;
    }

    async loadSeasons() {
        const response = await this.seasonService.getSeasons(this.companyId!);
        if (response.success) this.seasons = response.data;
    }

    async sendMembership() {
        if (this.isAthleteUser) return;
        this.submitted = true;
        const firstInvalid = MEMBERSHIP_FIELDS.find(field => this.errorFor(field));
        if (firstInvalid) {
            setTimeout(() => document.querySelector<HTMLElement>(`[data-field="${firstInvalid}"]`)?.focus());
            return;
        }

        this.saving = true;
        try {
            const payload: any = {
                ...this.membership,
                athleteId: this.athlete.id,
                registered: this.isRegistered,
                signed: this.isSigned,
            };
            const membershipResponse = await this.membershipService.createMembership(payload);

            if (membershipResponse?.success) {
                Swal.fire({icon: "success", title: "Tesseramento salvato con successo!"});
                this.router.navigate(this.backRoute);
            } else {
                Swal.fire({icon: "error", title: "Errore nel processo di tesseramento, riprova"});
            }
        } finally {
            this.saving = false;
        }
    }

    async sendMembershipNotification() {
        this.sendingNotification = true;
        try {
            const response = await this.membershipService.sendNotification(this.athleteId);
            if (response?.success) {
                Swal.fire({icon: "success", title: "Notifica email inviata con successo!"});
            } else {
                Swal.fire({icon: "error", title: "Errore nell'invio dell'email, riprova"});
            }
        } finally {
            this.sendingNotification = false;
        }
    }
}
